import { readSheet, Row } from 'read-excel-file/browser';

export type ActivityProfile =
  | 'industrial'
  | 'rural'
  | 'market'
  | 'public-service';

export type Gender = 'male' | 'female' | 'other';

export interface ParsedRow {
  rowNumber: number;
  nome: string;
  cpf: string;
  telefone: string;
  perfil: ActivityProfile | '';
  genero: Gender | '';
  errors: string[];
}

export interface ParseResult {
  rows: ParsedRow[];
  totalRows: number;
  validRows: number;
  invalidRows: number;
  fileErrors: string[];
}

type FieldKey = 'nome' | 'cpf' | 'telefone' | 'perfil' | 'genero';

const FIELD_ALIASES: Record<FieldKey, string[]> = {
  nome: ['nome', 'name', 'nome completo', 'full name', 'fullname'],
  cpf: ['cpf'],
  telefone: ['telefone', 'phone', 'celular', 'contato'],
  perfil: [
    'perfil',
    'categoria',
    'atividade',
    'activity_profile',
    'activityprofile',
    'perfil de atividade',
  ],
  genero: ['genero', 'gênero', 'gender', 'sexo'],
};

const REQUIRED_FIELDS: FieldKey[] = ['nome', 'cpf'];

const ACTIVITY_ALIASES: Record<string, ActivityProfile> = {
  industrial: 'industrial',
  industria: 'industrial',
  industria_alimenticia: 'industrial',
  rural: 'rural',
  market: 'market',
  comercio: 'market',
  mercado: 'market',
  'comercio em geral': 'market',
  'public-service': 'public-service',
  publicservice: 'public-service',
  'servico publico': 'public-service',
  'servicos publicos': 'public-service',
};

const GENDER_ALIASES: Record<string, Gender> = {
  male: 'male',
  masculino: 'male',
  m: 'male',
  female: 'female',
  feminino: 'female',
  f: 'female',
  other: 'other',
  outro: 'other',
  outros: 'other',
  o: 'other',
};

const normalize = (value: string): string =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

const isValidCpf = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  const calc = (base: string, factor: number): number => {
    let total = 0;
    for (let i = 0; i < base.length; i += 1) {
      total += parseInt(base[i], 10) * (factor - i);
    }
    const mod = (total * 10) % 11;
    return mod === 10 ? 0 : mod;
  };

  const d1 = calc(digits.slice(0, 9), 10);
  if (d1 !== parseInt(digits[9], 10)) return false;
  const d2 = calc(digits.slice(0, 10), 11);
  return d2 === parseInt(digits[10], 10);
};

const formatCpfMask = (digits: string): string =>
  digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

const cellToString = (cell: unknown): string => {
  if (cell === null || cell === undefined) return '';
  if (typeof cell === 'number') return String(cell);
  if (cell instanceof Date) return cell.toISOString();
  return String(cell).trim();
};

const resolveHeaderMap = (headerRow: Row): Map<FieldKey, number> => {
  const map = new Map<FieldKey, number>();
  headerRow.forEach((rawHeader, idx) => {
    const normalized = normalize(cellToString(rawHeader));
    if (!normalized) return;
    (Object.keys(FIELD_ALIASES) as FieldKey[]).forEach(field => {
      if (FIELD_ALIASES[field].includes(normalized)) {
        map.set(field, idx);
      }
    });
  });
  return map;
};

export async function parseBatchFile(file: File): Promise<ParseResult> {
  const rawRows = (await readSheet(file)) as Row[];
  const fileErrors: string[] = [];

  if (rawRows.length < 2) {
    return {
      rows: [],
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      fileErrors: ['A planilha está vazia ou não contém linhas de dados.'],
    };
  }

  const headerMap = resolveHeaderMap(rawRows[0]);
  const missing = REQUIRED_FIELDS.filter(f => !headerMap.has(f)).map(
    f => FIELD_ALIASES[f][0],
  );
  if (missing.length > 0) {
    fileErrors.push(
      `Colunas obrigatórias não encontradas no cabeçalho: ${missing.join(
        ', ',
      )}.`,
    );
  }

  const rows: ParsedRow[] = [];
  const seenCpfs = new Map<string, number>();

  for (let i = 1; i < rawRows.length; i += 1) {
    const row = rawRows[i];
    const rowNumber = i + 1;

    const getField = (field: FieldKey): string => {
      const col = headerMap.get(field);
      if (col === undefined) return '';
      return cellToString(row[col]);
    };

    const nome = getField('nome');
    const cpfRaw = getField('cpf');
    const telefone = getField('telefone');
    const perfilRaw = getField('perfil');
    const generoRaw = getField('genero');

    const isEmpty = !nome && !cpfRaw && !telefone && !perfilRaw && !generoRaw;
    if (isEmpty) {
      // skip empty rows
    } else {
    const errors: string[] = [];

    if (!nome) errors.push('Nome é obrigatório');

    const cpfDigits = cpfRaw.replace(/\D/g, '');
    let cpfMasked = '';
    if (!cpfRaw) {
      errors.push('CPF é obrigatório');
    } else if (cpfDigits.length !== 11 || !isValidCpf(cpfDigits)) {
      errors.push('CPF inválido');
    } else if (seenCpfs.has(cpfDigits)) {
      errors.push(`CPF duplicado (também na linha ${seenCpfs.get(cpfDigits)})`);
    } else {
      seenCpfs.set(cpfDigits, rowNumber);
      cpfMasked = formatCpfMask(cpfDigits);
    }

    let perfil: ActivityProfile | '' = '';
    if (perfilRaw) {
      const resolved = ACTIVITY_ALIASES[normalize(perfilRaw)];
      if (resolved) {
        perfil = resolved;
      } else {
        errors.push(
          `Perfil "${perfilRaw}" não reconhecido (use: Industrial, Rural, Comércio ou Serviço Público)`,
        );
      }
    }

    let genero: Gender | '' = '';
    if (generoRaw) {
      const resolved = GENDER_ALIASES[normalize(generoRaw)];
      if (resolved) {
        genero = resolved;
      } else {
        errors.push(
          `Gênero "${generoRaw}" não reconhecido (use: Masculino, Feminino ou Outro)`,
        );
      }
    }

    rows.push({
      rowNumber,
      nome,
      cpf: cpfMasked || cpfRaw,
      telefone,
      perfil,
      genero,
      errors,
    });
    }
  }

  const validRows = rows.filter(r => r.errors.length === 0).length;

  return {
    rows,
    totalRows: rows.length,
    validRows,
    invalidRows: rows.length - validRows,
    fileErrors,
  };
}
